import React, { useEffect, useState, useRef } from 'react';
import Login from './Login';
import jwt_decode, { jwtDecode } from "jwt-decode";
import NurseScheduleCard from './NurseScheduleCard';
import { Routes, Route, useNavigate, useParams } from 'react-router-dom';
import ProfilePage from './ProfilePage';
import Modal from './Modal';
import { BASE_API } from './constants';
import styles from './AppRoutes.module.css';
import PatientWorkflowPage from './PatientWorkflowPage';

interface Patient {
  id: string;
  demographics: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    age?: string;
    pictureUrl?: string;
    nickname?: string;
  };
  patientLastActiveTime?: string;
}

interface NurseDashboardCategory {
  category: {
    id: string;
    name: string;
    description: string;
    createdAt: string | null;
  };
  patients: Patient[];
}

function getTokenExpiry(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isTokenValid(token: string | null): boolean {
  if (!token) return false;
  const expiry = getTokenExpiry(token);
  return expiry ? Date.now() < expiry : false;
}

const AppRoutes: React.FC = () => {
  const [accessToken, setAccessToken] = useState<string | null>(() => localStorage.getItem('access_token'));
  const [idToken, setIdToken] = useState<string | null>(() => localStorage.getItem('id_token'));
  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [nurseId, setNurseId] = useState<string | null>(() => localStorage.getItem('nurseId'));
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [nurseDetails, setNurseDetails] = useState<any>(null);
  const [nurseCalendar, setNurseCalendar] = useState<any[]>([]);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [showProfileModal, setShowProfileModal] = useState(false);

  // On login success, store tokens and fetch nurseId
  const handleLoginSuccess = async (auth: { access_token: string; id_token?: string; expires_in: number; token_type: string }) => {
    setAccessToken(auth.access_token);
    if (auth.id_token) setIdToken(auth.id_token);
    setLoading(true);
    setLoginError(null);
    try {
      // Call nurse login API to get nurseId
      const response = await fetch(`${BASE_API}/auth/nurse/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': auth.id_token || auth.access_token,
        },
        body: JSON.stringify({ auth0Id: getAuth0IdFromToken(auth.id_token || auth.access_token) }),
      });
      const data = await response.json();
      if (data.nurseId) {
        setNurseId(data.nurseId);
        localStorage.setItem('nurseId', data.nurseId);
      } else {
        setLoginError('No nurseId returned from login API.');
        setNurseId(null);
        localStorage.removeItem('nurseId');
      }
    } catch (err: any) {
      setLoginError('Login failed. Please try again.');
      setNurseId(null);
      localStorage.removeItem('nurseId');
    } finally {
      setLoading(false);
    }
  };

  // Helper to extract auth0Id from id_token or access_token
  function getAuth0IdFromToken(token: string): string | null {
    try {
      const payload = jwtDecode(token);
      return payload.sub || null;
    } catch (e) {
      return null;
    }
  }

  // On logout, clear all tokens and nurseId
  const handleLogout = () => {
    setAccessToken(null);
    setIdToken(null);
    setNurseId(null);
    setPatients([]);
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('nurseId');
    localStorage.removeItem('expires_in');
    navigate('/');
  };

  // On app load, if token exists and is valid, fetch nurseId if not present
  useEffect(() => {
    if (accessToken && isTokenValid(accessToken) && !nurseId) {
      // Try to get nurseId from token
      const auth0Id = getAuth0IdFromToken(idToken || accessToken);
      if (auth0Id) {
        setLoading(true);
        fetch(`${BASE_API}/auth/nurse/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': idToken || accessToken,
          },
          body: JSON.stringify({ auth0Id }),
        })
          .then(res => res.json())
          .then(data => {
            if (data.nurseId) {
              setNurseId(data.nurseId);
              localStorage.setItem('nurseId', data.nurseId);
            } else {
              setLoginError('No nurseId returned from login API.');
              setNurseId(null);
              localStorage.removeItem('nurseId');
            }
          })
          .catch(() => {
            setLoginError('Login failed. Please try again.');
            setNurseId(null);
            localStorage.removeItem('nurseId');
          })
          .finally(() => setLoading(false));
      }
    }
  }, [accessToken, idToken, nurseId]);

  // Fetch dashboard when nurseId and token are available and valid
  useEffect(() => {
    if (nurseId && accessToken && isTokenValid(accessToken)) {
      setLoading(true);
      fetch(`${BASE_API}/nurses/${nurseId}/dashboard`, {
        headers: {
          'x-access-token': idToken || accessToken,
        },
      })
        .then(res => res.json())
        .then(data => {
          const allPatients: Patient[] = (data.nursePatientsByCategory || []).flatMap((cat: NurseDashboardCategory) => cat.patients);
          setPatients(allPatients);
        })
        .catch(() => setLoginError('Failed to fetch dashboard.'))
        .finally(() => setLoading(false));
    }
  }, [nurseId, accessToken, idToken]);

  // Fetch nurse availability after login
  useEffect(() => {
    if (nurseId && accessToken && isTokenValid(accessToken)) {
      fetch(`${BASE_API}/nurses/${nurseId}/availability`, {
        headers: {
          'x-access-token': idToken || accessToken,
        },
      })
        .then(res => res.json())
        .then(data => {
          setNurseDetails(data.auth0NurseDetails);
          setNurseCalendar(data.calendar || []);
        })
        .catch(() => {
          setNurseDetails(null);
          setNurseCalendar([]);
        });
    }
  }, [nurseId, accessToken, idToken]);

  // If token is expired, force logout
  useEffect(() => {
    if (accessToken && !isTokenValid(accessToken)) {
      handleLogout();
    }
  }, [accessToken]);

  // Close menu on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
    }
    if (profileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileMenuOpen]);

  // Default to first patient if none selected
  useEffect(() => {
    if (!selectedPatient && patients.length > 0) {
      setSelectedPatient(patients[0]);
    }
  }, [patients, selectedPatient]);

  if (!accessToken || !isTokenValid(accessToken) || !nurseId) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  if (loginError) {
    return (
      <div className={styles.loginErrorContainer}>
        <div className={styles.loginErrorCard}>
          <h2 className={styles.loginErrorTitle}>Login Error</h2>
          <p className={styles.loginErrorMessage}>{loginError}</p>
          <button onClick={handleLogout} className={styles.loginErrorButton}>Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={
          <div className={styles.appBackground}>
            {/* Sleek Top Header */}
            <header className={styles.header}>
              <div className={styles.headerLeft}>
                <img src="/app_logo.png" alt="App Logo" className={styles.headerLogo} />
                <span className={styles.headerTitle}>Physician Portal</span>
              </div>
              <div className={styles.headerProfile}>
                <div
                  className={styles.profileMenuButtonContainer}
                  onClick={() => setProfileMenuOpen(v => !v)}
                  ref={profileMenuRef}
                >
                  {nurseDetails?.pictureUrl ? (
                    <img src={nurseDetails.pictureUrl} alt="Profile" className={styles.profileMenuButtonImage} />
                  ) : (
                    <div className={styles.profileMenuButtonImagePlaceholder}>
                      {nurseDetails?.firstName?.[0] || '?'}
                    </div>
                  )}
                  <svg width="18" height="18" fill="#1976d2" className={styles.profileDownIcon} viewBox="0 0 24 24"><path d="M7 10l5 5 5-5z"/></svg>
                </div>
                {profileMenuOpen && (
                  <div className={styles.profileMenu} onClick={e => { console.log('Dropdown menu clicked'); e.stopPropagation(); }}>
                    <button onMouseDown={() => { console.log('Edit Profile mousedown'); setShowProfileModal(true); }} className={styles.profileMenuButton}>Edit Profile</button>
                    <button onMouseDown={() => { setProfileMenuOpen(false); setTimeout(handleLogout, 0); }} className={`${styles.profileMenuButton} ${styles.profileMenuButtonLogout}`}>Logout</button>
                  </div>
                )}
              </div>
            </header>
            {/* 3-Column Layout */}
            <div className={styles.dashboardContainer}>
              <div className={styles.dashboardThreeColRow}>
                {/* Left Panel: Patient List */}
                <div className={styles.leftPanel}>
                  <h3 className={styles.patientListTitle}>Patients</h3>
                  {patients.length === 0 ? (
                    <div className={styles.patientListEmpty}>No patients linked to this provider.</div>
                  ) : (
                    <div className={styles.flexGrow}>
                      <ul className={styles.patientList}>
                        {patients.map((patient) => {
                          const isSelected = selectedPatient?.id === patient.id;
                          return (
                            <li
                              key={patient.id}
                              className={
                                styles.patientListItem + (isSelected ? ' ' + styles.patientListItemSelected : '')
                              }
                              onClick={() => setSelectedPatient(patient)}
                            >
                              {patient.demographics?.pictureUrl ? (
                                <img src={patient.demographics.pictureUrl} alt={patient.demographics.fullName || 'Patient'} className={styles.patientAvatarSmall} />
                              ) : (
                                <div className={styles.patientAvatarPlaceholder}>
                                  {patient.demographics?.firstName?.[0] || '?'}
                                </div>
                              )}
                              <div style={{ flex: 1 }}>
                                <div className={styles.patientListName + (isSelected ? ' ' + styles.patientListNameSelected : '')}>
                                  {patient.demographics?.fullName || patient.demographics?.nickname || 'Unknown'}
                                </div>
                                {patient.demographics?.age && (
                                  <div className={styles.patientListAge + (isSelected ? ' ' + styles.patientListAgeSelected : '')}>
                                    Age: {patient.demographics.age}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
                {/* Center Panel: Patient Workflow */}
                <div className={styles.centerPanel}>
                  {selectedPatient && <PatientWorkflowPage key={selectedPatient.id} patient={selectedPatient} nurseCalendar={nurseCalendar} />}
                </div>
                {/* Right Panel: Physician Schedule */}
                <div className={styles.rightPanel}>
                  {nurseDetails ? (
                    <NurseScheduleCard nurseDetails={nurseDetails} calendar={nurseCalendar} nurseId={nurseId} accessToken={idToken || accessToken} onProfileUpdate={updated => setNurseDetails(updated)} />
                  ) : (
                    <div className={styles.nurseScheduleCard}>
                      Loading schedule...
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        } />
        <Route path="/patients/:id/workflow" element={<PatientWorkflowPageWrapper />} />
      </Routes>
      {showProfileModal && (
        <Modal onClose={() => setShowProfileModal(false)}>
          <ProfilePage nurseDetails={nurseDetails} nurseId={nurseId} accessToken={idToken || accessToken} onProfileUpdate={updated => { setNurseDetails(updated); setShowProfileModal(false); }} />
        </Modal>
      )}
    </>
  );
};

const PatientWorkflowPageWrapper: React.FC = () => {
  const { id } = useParams();
  // Access patients from the main AppRoutes state
  // We'll need to pass patients as a prop to this wrapper, so let's lift it up
  // But for now, let's use a workaround: get patients from localStorage if possible (or return null)
  // Ideally, this should be refactored to use context or a global state
  // For now, return null if not found
  // (You may want to refactor this to use context or a global store for patients)
  return null;
};

export default AppRoutes; 